import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/Layout";
import { AdminProvider, useAdmin } from "@/context/AdminContext";
import { useEffect } from "react";
import axios from "axios";

// Public Pages
import HomePage from "@/pages/HomePage";
import CaliforniaPage from "@/pages/CaliforniaPage";
import ArizonaPage from "@/pages/ArizonaPage";
import GeorgiaPage from "@/pages/GeorgiaPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import FAQsPage from "@/pages/FAQsPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";

// Admin Pages
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminSocialMedia from "@/pages/admin/AdminSocialMedia";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminEmailSequences from "@/pages/admin/AdminEmailSequences";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminBlog from "@/pages/admin/AdminBlog";
import FeatureDocumentation from "@/pages/admin/FeatureDocumentation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Track page views
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    // Don't track admin pages
    if (location.pathname.startsWith('/admin')) return;

    const trackPageView = async () => {
      try {
        await axios.post(`${API}/analytics/pageview`, {
          page: location.pathname,
          referrer: document.referrer || null
        });
      } catch (error) {
        // Silently fail
      }
    };
    trackPageView();
  }, [location.pathname]);

  return null;
}

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAdmin();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return <AdminLayout>{children}</AdminLayout>;
}

function App() {
  return (
    <div className="min-h-screen bg-slate-50">
      <AdminProvider>
        <BrowserRouter>
          <PageViewTracker />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Layout><HomePage /></Layout>} />
            <Route path="/california" element={<Layout><CaliforniaPage /></Layout>} />
            <Route path="/arizona" element={<Layout><ArizonaPage /></Layout>} />
            <Route path="/georgia" element={<Layout><GeorgiaPage /></Layout>} />
            <Route path="/about" element={<Layout><AboutPage /></Layout>} />
            <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
            <Route path="/faqs" element={<Layout><FAQsPage /></Layout>} />
            <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
            <Route path="/blog/:slug" element={<Layout><BlogPostPage /></Layout>} />
            <Route path="/privacy-policy" element={<Layout><PrivacyPolicyPage /></Layout>} />
            <Route path="/terms-of-service" element={<Layout><TermsOfServicePage /></Layout>} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/contacts" element={<ProtectedRoute><AdminContacts /></ProtectedRoute>} />
            <Route path="/admin/social-media" element={<ProtectedRoute><AdminSocialMedia /></ProtectedRoute>} />
            <Route path="/admin/media" element={<ProtectedRoute><AdminMedia /></ProtectedRoute>} />
            <Route path="/admin/email-sequences" element={<ProtectedRoute><AdminEmailSequences /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
            <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
            <Route path="/admin/documentation" element={<ProtectedRoute><FeatureDocumentation /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AdminProvider>
    </div>
  );
}

export default App;
